import { Flex } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useDarkMode } from '../../hooks/useDarkMode';
import { isSdkDocVersion, packageDocsUrl } from '../../../typedoc/sdk-doc-versions';
import { useVersion } from '../../versions/url-version';

export function SDK() {
  const version = useVersion();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isDarkMode = useDarkMode();

  // Apply theme to iframe when it loads or when dark mode changes
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const applyTheme = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        // TypeDoc uses dataset.theme on documentElement
        // Values can be 'os', 'light', or 'dark'
        // Since iframe doesn't inherit OS preference, we set it explicitly
        const theme = isDarkMode ? 'dark' : 'light';
        iframeDoc.documentElement.dataset.theme = theme;

        // Also update localStorage for persistence across navigation
        try {
          const iframeWindow = iframe.contentWindow;
          if (iframeWindow?.localStorage) {
            iframeWindow.localStorage.setItem('tsd-theme', theme);
          }
        } catch {
          // Ignore localStorage errors (e.g., if blocked)
        }

        // Trigger TypeDoc's theme system if available
        const iframeWindow = iframe.contentWindow as Window & { app?: { setTheme?: (theme: string) => void } };
        if (iframeWindow?.app?.setTheme) {
          iframeWindow.app.setTheme(theme);
        }
      } catch (error) {
        // Cross-origin restrictions - fallback to postMessage
        console.warn('Could not access iframe document directly, using postMessage:', error);
        try {
          iframe.contentWindow?.postMessage(
            {
              type: 'set-theme',
              theme: isDarkMode ? 'dark' : 'light',
            },
            '*',
          );
        } catch {
          // Ignore postMessage errors
        }
      }
    };

    const handleLoad = () => {
      // Small delay to ensure TypeDoc's scripts have initialized
      setTimeout(applyTheme, 200);
    };

    // Apply theme when iframe loads
    iframe.addEventListener('load', handleLoad);

    // Apply immediately if already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      handleLoad();
    }

    // Also apply when dark mode changes (iframe might already be loaded)
    applyTheme();

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [isDarkMode]);

  if (!version || !isSdkDocVersion(version)) {
    return <div>Package API reference is not available for this version</div>;
  }

  return (
    <Flex h="calc(100vh - 120px - 32px)">
      <iframe
        key={version}
        ref={iframeRef}
        src={`${packageDocsUrl(version)}/index.html`}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
        title="SDK Documentation"
      />
    </Flex>
  );
}
